import React from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { Link, Heading, Section, Text, Notification } from 'shared-library';
import { formatUnixDatetime } from 'packages/formatters';
import { getEnvironment } from 'packages/locations';
import { fetchRosettaResource } from 'packages/apis';
import { Row, Column, Label } from '../Layout';
import { buildStudySelector } from '../../store/studies/selectors';
import { selectStaff } from '../../store/staff/selectors';
import { selectSession } from '../../store/session/selectors';
import { captureError } from '../../lib';

const ReferralMedicalHistory = () => {
  const { studyId: selectedStudyId } = useParams();
  const { addToast } = useToasts();
  const selectStudy = buildStudySelector(selectedStudyId);
  const study = useSelector(selectStudy);
  const staff = useSelector(selectStaff);
  const { data: session } = useSelector(selectSession);
  // TODO: Remove env check after implementing the new Auth0 env
  const isNonProdEnvironment = getEnvironment() !== 'PRODUCTION';
  const hasHiPermission = session.roles.includes('hi') || isNonProdEnvironment;

  const reminderMedicalHistorySMSSentBy = study.status.reminderMedicalHistorySMSSentBy
    ? staff.data.filter(
        (member) => member.userId === study.status.reminderMedicalHistorySMSSentBy,
      )[0]
    : null;
  const reminderMedicalHistoryEmailSentBy = study.status.reminderMedicalHistoryEmailSentBy
    ? staff.data.filter(
        (member) => member.userId === study.status.reminderMedicalHistoryEmailSentBy,
      )[0]
    : null;

  // Download PDF from base64 using data URI
  const downloadPdfFromBase64 = (base64Content: string, templateName: string) => {
    if (!study.studyID) {
      throw new Error('Study ID is required');
    }

    const dataUri = `data:application/pdf;base64,${base64Content}`;
    const link = document.createElement('a');

    link.href = dataUri;
    link.download = `${templateName}-${study.studyID}.pdf`;
    link.click();
  };

  const downloadConsentDocument = async (templateName: string, errorMessage: string) => {
    try {
      if (!study.studyID) {
        throw new Error('Study ID is required');
      }

      const entityId = study.poetryMetadata?.carePlanId || study.studyID;
      const response = await fetchRosettaResource(
        `/api/v1/consent/documents/download/${study.patient}/${entityId}`,
      );

      const base64Content = response.parsedBody?.file || response.parsedBody?.documents?.[0]?.file;

      if (!base64Content) {
        throw new Error('No file content found in response');
      }

      downloadPdfFromBase64(base64Content, templateName);

      return true;
    } catch (error) {
      captureError(error);
      addToast(
        Notification.create('Download failed', errorMessage),
        { appearance: 'error' },
      );
      return false;
    }
  };

  const downloadBlankConsentForm = () =>
    downloadConsentDocument(
      'blank-consent-form',
      'Something went wrong while downloading the blank consent form. Give it another try and please report an issue if this persists.',
    );

  const downloadConsent = () =>
    downloadConsentDocument(
      'patient-consent',
      'Something went wrong while downloading the member agreement and consent. Give it another try and please report an issue if this persists.',
    );

  return (
    <>
      <Section title="Medical History">
        <Heading size={7}>Medical history</Heading>
        <Row>
          <Column>
            <Label>MH Completed</Label>
            <Text>
              {study.status.medicalHistoryTime
                ? formatUnixDatetime(study.status.medicalHistoryTime)
                : 'Not completed'}
            </Text>
          </Column>
          <Column>
            <Label>MH Reminder email sent</Label>
            {study.status.reminderMedicalHistoryEmailSentTime ? (
              <>
                <Text after={0}>
                  {formatUnixDatetime(
                    study.status.reminderMedicalHistoryEmailSentTime,
                    study.location.timezone,
                  )}
                </Text>
                <Text>
                  {study.status.reminderMedicalHistoryEmailSentBy
                    ? `${reminderMedicalHistoryEmailSentBy.firstname} ${reminderMedicalHistoryEmailSentBy.lastname}`
                    : 'System'}
                </Text>
              </>
            ) : (
              <Text>---</Text>
            )}
          </Column>
          <Column>
            <Label>MH Reminder SMS sent</Label>
            {study.status.reminderMedicalHistorySMSSentTime ? (
              <>
                <Text after={0}>
                  {formatUnixDatetime(
                    study.status.reminderMedicalHistorySMSSentTime,
                    study.location.timezone,
                  )}
                </Text>
                <Text>
                  {study.status.reminderMedicalHistorySMSSentBy
                    ? `${reminderMedicalHistorySMSSentBy.firstname} ${reminderMedicalHistorySMSSentBy.lastname}`
                    : 'System'}
                </Text>
              </>
            ) : (
              <Text>---</Text>
            )}
          </Column>
        </Row>
        {study.status.medicalHistory && (
          <Row>
            {hasHiPermission && (
              <Column>
                <Link
                  target="_blank"
                  href={`/admin/study/${study.studyID}/medical-history/radiologist`}
                >
                  View medical history
                </Link>
              </Column>
            )}
            {(study.memberConsentSigned || study.technologistConsentSigned) && (
              <Column>
                <Link onClick={downloadConsent}> Download member agreement and consent</Link>
              </Column>
            )}
          </Row>
        )}
        {!study.memberConsentSigned && (
          <Row>
            <Column>
              <Link onClick={downloadBlankConsentForm}> Download Blank Consent Form</Link>
            </Column>
          </Row>
        )}
      </Section>
    </>
  );
};

export default ReferralMedicalHistory;
